import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import styles from './styles/CleverTable.module.scss';
import {CSSTransition} from "react-transition-group";
import classNames from 'classnames';
import InfiniteScroller from "../../header/InfiniteScroller";
import TrackVisibility from "react-on-screen";
import {useMachine} from "@xstate/react";
import {FetchMachine} from "../../machines/FetchMachine";
import FontAwesome from "../FontAwesome";
import Scrollbar from "react-scrollbars-custom";

export default function CleverTable(props) {
    const {transitionIn = true} = props;
    const [columnWidths, setColumnWidths] = useState(props.columns.map(c => c.initialWidth));

    const [dragging, setDragging] = useState(false);
    const [currentDragColumn, setCurrentDragColumn] = useState(null);
    const [dragStart, setDragStart] = useState(null);
    const [initialWidths, setInitialWidths] = useState(null);
    const [currentDragPosition, setCurrentDragPosition] = useState(null);
    const [current, send] = useMachine(FetchMachine);

    async function handleFetchMore() {
        send("FETCH");
        await props.handleFetchMore();
        send("FULFILL");
    }

    const tableRef = useRef(null);

    useEffect(() => {
        if (!dragging) {
            setColumnWidths(props.columns.map(c => c.initialWidth))
        }
    }, [props.columns]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [dragging]);

    useEffect(() => {
        let dragged; // positive if dragged left, negative if dragged right
        if (props.initialWidthIsPercent) {
            dragged = ((dragStart - currentDragPosition) / tableRef.current?.clientWidth) * 100;
        } else {
            dragged = dragStart - currentDragPosition;
        }

        if (typeof currentDragColumn === 'number' && currentDragPosition) {
            handleDragColumn(dragged);
        }

    }, [currentDragPosition]);

    const handleDragColumn = useCallback(dragged => {
        setColumnWidths(current => {
            return current.map((c, i) => {
                if (i === currentDragColumn) {
                    return initialWidths[i] - dragged;
                } else if (i === currentDragColumn + 1) {
                    return initialWidths[i] + dragged;
                }
                return initialWidths[i];
            });
        });
    }, [columnWidths, initialWidths, currentDragColumn]);

    const handleMouseDown = useCallback((e, columnIndex) => {
        if (e.button === 0) {
            setDragging(true);
            setCurrentDragColumn(columnIndex);
            setDragStart(e.screenX);
            setInitialWidths(columnWidths);
        }
    }, [columnWidths]);

    const handleMouseUp = useCallback(() => {
        if (dragging) {
            setDragging(false);
            setCurrentDragColumn(null);
            setInitialWidths(null);
            setCurrentDragPosition(null);
            setDragStart(null);
        }
    }, [dragging]);

    const handleMouseMove = useCallback(e => {
        if (dragging) {
            setCurrentDragPosition(e.screenX);
        }
    }, [dragging]);

    const {columnDividers, cumulativeWidth} = useMemo(() => {
        let cumulativeWidth = 0;
        const cols = props.columns.reduce((acc, curr, idx) => {
            cumulativeWidth += columnWidths[idx] ;

            if (idx < props.columns.length - 1) { // don't add a divider for the last column
                acc.push(<span
                    key={curr.dataKey}
                    onMouseDown={e => handleMouseDown(e, idx)}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    style={{
                        left: cumulativeWidth + (props.initialWidthIsPercent ? "%" : "px"),
                        height: props.height * (props.data.length + 1),
                    }}
                />);
            }

            return acc;
        }, []);
        return {columnDividers: cols, cumulativeWidth};
    }, [props.columns.length, props.data.length, props.initialWidthIsPercent, props.height, columnWidths]);

    const containerClassName = classNames({
        [styles.table]: true,
        [props.className]: true,
    });

    const tableRowClassName = classNames({
        [styles.tableRow]: true,
        [props.tableRowClassName]: props.tableRowClassName,
    })

    return <div className={containerClassName} ref={tableRef} style={{
        height: (props.data.length + 1 + (props.data.length === 0 || props.shouldFetchMore ? 1 : 0)) * props.height,
        width: props.initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`,
        maxHeight: props.maxHeight,
    }}>
        <div className={styles.columnDividerContainer}>
            {columnDividers}
        </div>
        <div className={styles.tableRow} style={{
            width: props.initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`, // 4rem handles the margin of each row
        }}>
            {props.columns.map((c, i) => {
                let width;
                if (props.initialWidthIsPercent) {
                    // account for scrollbar of 10px
                    width = `calc(${columnWidths[i]}% - ${10 / props.columns.length}px)`;
                } else {
                    width = columnWidths[i] + "px";
                }
                return <div
                    className={styles.tableCell}
                    style={{
                        width,
                        height: props.height,
                    }}
                    key={c.dataKey}
                >{c.title}</div>
            })}
        </div>
        {props.data.length === 0 && props.noDataMessage && <div style={{
            textAlign: 'center',
            paddingTop: '1rem',
            width: '100%',
        }}>
            {props.noDataMessage}
        </div>}
        <Scrollbar
            scrollerProps={{
                style: {marginTop: "-50px"},
            }}
            permanentTrackY={true}
            style={{height: "calc(100% - 50px)"}}
        >
            {props.data.map((d, i) => {
                return <CSSTransition
                    appear
                    key={d.key}
                    data-selected={d.key === props.selectedItemKey}
                    in={transitionIn}
                    timeout={250}
                    classNames={props.transitionClassNames}>
                    <div
                        className={tableRowClassName}
                        tabIndex={d.onClick ? 0 : -1}
                        role={d.onClick ? "button" : "none"}
                        style={{
                            transform: `translateY(${(i * props.height) + props.height}px)`,
                            height: props.height,
                            width: props.initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`,
                            cursor: d.onClick ? "pointer" : "inherit",
                            ...d.style,
                        }}
                        onKeyDown={e => {
                            if (e.which === 13 && d.onClick) {
                                d.onClick();
                            }
                        }}
                        onClick={() => {
                            if (d.onClick) d.onClick();
                        }}
                    >
                        {props.columns.map((c, i) => <div className={styles.tableCell}
                                                          key={c.dataKey}
                                                          style={{
                                                              width: columnWidths[i] + (props.initialWidthIsPercent ? "%" : "px"),
                                                              height: props.height
                                                          }}>{d[c.dataKey]}</div>)}
                    </div>
                </CSSTransition>
            })}
            <CSSTransition timeout={250} key={'loadmore'} appear in={true} classNames={props.transitionClassNames}>
                {(props.shouldFetchMore && props.handleFetchMore) ? <div style={{
                    transform: `translateY(${(props.data.length * 50) + 50}px)`,
                    textAlign: 'center',
                    height: props.height,
                    lineHeight: props.height + "px",
                }}>
                    {!props.useInfiniteScroll &&
                        <button disabled={current.value === "PENDING"} onClick={handleFetchMore}>
                            {current.value === "PENDING" ?
                                <FontAwesome spin prefix={'fas'} name={'fa-spinner'}/> : "Load More"}
                        </button>}
                    {props.useInfiniteScroll && <TrackVisibility throttleInterval={2000} offset={275}>
                        <InfiniteScroller fetchMore={props.handleFetchMore}/>
                    </TrackVisibility>}
                </div> : <div/>}
            </CSSTransition>
        </Scrollbar>
    </div>
}

CleverTable.defaultProps = {
    data: [],
    columns: [],
    height: 50,
    className: "",
    fetchMoreDataComponent: <div/>,
    fetchMoreData: false,
    maxHeight: 400,
    initialWidthIsPercent: false,
    useInfiniteScroll: false,
};

CleverTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columns: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        dataKey: PropTypes.string.isRequired,
        initialWidth: PropTypes.number, // either absolute or percent value, all columns must add up to 100 if using percent
    })),
    initialWidthIsPercent: PropTypes.bool, // pass true if the initial widths should be treated as percents (must be numbers)
    height: PropTypes.number,
    className: PropTypes.string,
    tableRowClassName: PropTypes.string,
    noDataMessage: PropTypes.string,
    handleFetchMore: PropTypes.func,
    shouldFetchMore: PropTypes.bool,
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedItemKey: PropTypes.any,
    transitionIn: PropTypes.bool,
    transitionClassNames: PropTypes.any,
    useInfiniteScroll: PropTypes.bool,
};
